/**
 * API client for fetching UFC data
 *
 * This provides a thin wrapper around fetch with consistent
 * error handling and typed responses.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

export interface ApiError {
  message: string
  status?: number
}

export async function apiGet<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    const error: ApiError = {
      message: `Request failed: ${response.statusText}`,
      status: response.status
    }
    throw error
  }

  const contentType = response.headers.get('content-type')
  if (!contentType || !contentType.includes('application/json')) {
    const error: ApiError = {
      message: 'API endpoint not available',
      status: response.status
    }
    throw error
  }

  return response.json()
}

export async function apiPost<T>(endpoint: string, data: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })

  if (!response.ok) {
    const error: ApiError = {
      message: `Request failed: ${response.statusText}`,
      status: response.status
    }
    throw error
  }

  const contentType = response.headers.get('content-type')
  if (!contentType || !contentType.includes('application/json')) {
    const error: ApiError = {
      message: 'API endpoint not available',
      status: response.status
    }
    throw error
  }

  return response.json()
}

export async function apiPut<T>(endpoint: string, data: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })

  if (!response.ok) {
    const error: ApiError = {
      message: `Request failed: ${response.statusText}`,
      status: response.status
    }
    throw error
  }

  const contentType = response.headers.get('content-type')
  if (!contentType || !contentType.includes('application/json')) {
    const error: ApiError = {
      message: 'API endpoint not available',
      status: response.status
    }
    throw error
  }

  return response.json()
}

export async function apiDelete<T = void>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    const error: ApiError = {
      message: `Request failed: ${response.statusText}`,
      status: response.status
    }
    throw error
  }

  // Handle 204 No Content responses that don't return a body
  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get('content-type')
  if (!contentType || !contentType.includes('application/json')) {
    const error: ApiError = {
      message: 'API endpoint not available',
      status: response.status
    }
    throw error
  }

  return response.json()
}

export const api = {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  delete: apiDelete
}

// =============================================================================
// Synced Data API Functions
// =============================================================================

import type { SyncedFightOdds, SyncedFighterRanking } from '@/types'

/**
 * Get all active synced odds from the database
 * Returns odds for upcoming fights (within the last day)
 */
export async function getSyncedOdds(): Promise<SyncedFightOdds[]> {
  return apiGet<SyncedFightOdds[]>('/synced-odds')
}

/**
 * Get synced odds for a specific fight by fight ID
 */
export async function getSyncedOddsForFight(fightId: number): Promise<SyncedFightOdds> {
  return apiGet<SyncedFightOdds>(`/synced-odds/${fightId}`)
}

/**
 * Get synced odds by fighter names
 * Searches for odds matching the two fighters (order doesn't matter)
 */
export async function getSyncedOddsByFighters(fighter1: string, fighter2: string): Promise<SyncedFightOdds> {
  const params = new URLSearchParams({ fighter1, fighter2 })
  return apiGet<SyncedFightOdds>(`/synced-odds/fight?${params.toString()}`)
}

/**
 * Get all synced rankings from the database
 */
export async function getSyncedRankings(): Promise<SyncedFighterRanking[]> {
  return apiGet<SyncedFighterRanking[]>('/synced-rankings')
}

/**
 * Get synced rankings for a specific weight class
 */
export async function getSyncedRankingsByWeightClass(weightClass: string): Promise<SyncedFighterRanking[]> {
  return apiGet<SyncedFighterRanking[]>(`/synced-rankings/${encodeURIComponent(weightClass)}`)
}

/**
 * Get synced men's pound-for-pound rankings
 */
export async function getSyncedP4PRankingsMen(): Promise<SyncedFighterRanking[]> {
  return apiGet<SyncedFighterRanking[]>('/synced-rankings/p4p/men')
}

/**
 * Get synced women's pound-for-pound rankings
 */
export async function getSyncedP4PRankingsWomen(): Promise<SyncedFighterRanking[]> {
  return apiGet<SyncedFighterRanking[]>('/synced-rankings/p4p/women')
}
